import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { tools, getCategories } from '../data/tools';
import { articles, getFeaturedArticle, getArticlesByCategory, getArticleCategories, formatLastUpdated } from '../data/articles';
import { getAllSystems } from '../data/systems';
import { getToolBySlug } from '../data/tools';

type ViewMode = 'tools' | 'news' | 'flows';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = (searchParams.get('view') as ViewMode) || 'tools';
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);

  const handleSetViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    setSearchParams({ view: mode });
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedToolCategories, setSelectedToolCategories] = useState<string[]>([]);
  const [selectedNewsCategory, setSelectedNewsCategory] = useState('All');
  const [freeOnly, setFreeOnly] = useState<boolean | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toolCategories = getCategories();
  const newsCategories = getArticleCategories();
  const featuredArticle = getFeaturedArticle();
  const flows = getAllSystems();

  // Filter tools
  const hasFreeOption = (tool: typeof tools[0]) =>
    tool.pricing.tiers.some(t => t.price.startsWith('$0'));

  const filteredTools = tools.filter(tool => {
    const matchesSearch = tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tool.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedToolCategories.length === 0 || tool.categories.some(c => selectedToolCategories.includes(c));
    const matchesFree = freeOnly === null || (freeOnly ? hasFreeOption(tool) : !hasFreeOption(tool));
    return matchesSearch && matchesCategory && matchesFree;
  });

  const activeFilterCount = (selectedToolCategories.length > 0 ? 1 : 0) + (freeOnly !== null ? 1 : 0);

  // Filter articles
  const filteredArticles = getArticlesByCategory(selectedNewsCategory);

  return (
    <>
      <Helmet>
        <title>ChooseAI — Find the Perfect AI Tool</title>
        <meta name="description" content="ChooseAI helps founders and creators build the perfect AI stack. Browse and compare 20+ AI tools across writing, video, voice, coding, design, and more." />
        <meta property="og:title" content="ChooseAI — Find the Perfect AI Tool" />
        <meta property="og:description" content="ChooseAI helps founders and creators build the perfect AI stack. Browse and compare 20+ AI tools across writing, video, voice, coding, design, and more." />
        <meta property="og:url" content="https://chooseai.co.uk/" />
        <link rel="canonical" href="https://chooseai.co.uk/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "ChooseAI",
          "url": "https://chooseai.co.uk",
          "description": "ChooseAI helps founders and creators build the perfect AI stack. Browse and compare 20+ AI tools across writing, video, voice, coding, design, and more.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://chooseai.co.uk/?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "ChooseAI",
          "url": "https://chooseai.co.uk",
          "logo": "https://chooseai.co.uk/chooseai_logos/chooseai-banner.png",
          "description": "Independent AI tool discovery and comparison platform for founders, creators, and small businesses.",
          "email": "hello@chooseai.co.uk",
          "sameAs": ["https://www.linkedin.com/in/choose-ai"]
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero" style={{ padding: '6rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <img 
            src="/chooseai_logos/chooseai-banner-no-bg-dark-tight.png" 
            alt="ChooseAI"
            style={{ height: '120px', marginBottom: 'var(--space-xl)' }}
          />
          <h1 style={{ fontSize: '2.5rem', marginBottom: 'var(--space-lg)' }}>
            Building your AI stack shouldn't feel like guesswork.
          </h1>
          <p style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto var(--space-xl) auto', lineHeight: '1.8' }}>
            ChooseAI helps founders and creators build the perfect AI stack. We provide data-backed comparisons of tools like ChatGPT, Claude, and Github Copilot. Our guides focus on real-world use cases to drive adoption and simplify the ever-changing AI landscape for modern decision-makers.
          </p>
          
          {/* Toggle Buttons */}
          <div className="toggle-group">
            <button
              onClick={() => handleSetViewMode('tools')}
              className={viewMode === 'tools' ? 'button button-primary' : 'button'}
              style={{
                borderRadius: '0',
                backgroundColor: viewMode === 'tools' ? 'var(--color-primary)' : 'transparent',
                color: viewMode === 'tools' ? 'var(--color-bg)' : 'var(--color-primary)',
                border: 'none',
                padding: 'var(--space-md) var(--space-lg)',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}
            >
              ChooseTools
            </button>
            <div className="divider" />
            <button
              onClick={() => handleSetViewMode('news')}
              className={viewMode === 'news' ? 'button button-primary' : 'button'}
              style={{
                borderRadius: '0',
                backgroundColor: viewMode === 'news' ? 'var(--color-primary)' : 'transparent',
                color: viewMode === 'news' ? 'var(--color-bg)' : 'var(--color-primary)',
                border: 'none',
                padding: 'var(--space-md) var(--space-lg)',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}
            >
              ChooseNews
            </button>
            <div className="divider" />
            <button
              onClick={() => handleSetViewMode('flows')}
              className={viewMode === 'flows' ? 'button button-primary' : 'button'}
              style={{
                borderRadius: '0',
                backgroundColor: viewMode === 'flows' ? 'var(--color-primary)' : 'transparent',
                color: viewMode === 'flows' ? 'var(--color-bg)' : 'var(--color-primary)',
                border: 'none',
                padding: 'var(--space-md) var(--space-lg)',
                fontSize: '0.95rem',
                fontWeight: '600'
              }}
            >
              ChooseFlows
            </button>
          </div>
        </div>
      </section>

      {/* Content Section - ChooseTools */}
      {viewMode === 'tools' && (
        <section style={{ padding: '2rem 0 4rem 0' }}>
          <div className="container">
            {/* Search and Filter */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'stretch' }}>
                <input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 'var(--space-sm) var(--space-md)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--border-radius)',
                    backgroundColor: 'var(--color-card)',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-primary)',
                    fontSize: '1rem'
                  }}
                />
                <div ref={filterRef} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setFilterOpen(o => !o)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)',
                      padding: 'var(--space-sm) var(--space-md)',
                      border: `1px solid ${activeFilterCount > 0 ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      borderRadius: 'var(--border-radius)',
                      backgroundColor: activeFilterCount > 0 ? 'var(--color-primary)' : 'var(--color-card)',
                      color: activeFilterCount > 0 ? 'var(--color-bg)' : 'var(--color-text-primary)',
                      fontFamily: 'var(--font-primary)',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all var(--transition-speed)'
                    }}
                  >
                    <span>⚙ Filters</span>
                    {activeFilterCount > 0 && (
                      <span style={{
                        backgroundColor: 'var(--color-bg)',
                        color: 'var(--color-primary)',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        padding: '1px 7px',
                        lineHeight: '1.6'
                      }}>
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  {/* Dropdown panel */}
                  {filterOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        right: 0,
                        width: '280px',
                        backgroundColor: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--border-radius)',
                        padding: 'var(--space-lg)',
                        zIndex: 100,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                      }}
                    >
                      {/* Category */}
                      <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '700', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          Category
                        </label>
                        {/* Selected chips */}
                        {selectedToolCategories.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
                            {selectedToolCategories.map(cat => (
                              <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'var(--color-secondary)', color: 'var(--color-bg)', fontSize: '0.78rem', fontWeight: '600' }}>
                                {cat}
                                <button onClick={() => setSelectedToolCategories(prev => prev.filter(c => c !== cat))} style={{ background: 'none', border: 'none', color: 'var(--color-bg)', cursor: 'pointer', padding: '0', lineHeight: 1, fontSize: '0.85rem' }}>✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Search input */}
                        <input
                          type="text"
                          placeholder="Search categories..."
                          value={categorySearch}
                          onChange={e => setCategorySearch(e.target.value)}
                          style={{ width: '100%', padding: 'var(--space-xs) var(--space-sm)', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius)', backgroundColor: 'var(--color-bg)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-primary)', fontSize: '0.9rem', marginBottom: 'var(--space-xs)', boxSizing: 'border-box' }}
                        />
                        {/* Scrollable list */}
                        <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--border-radius)', backgroundColor: 'var(--color-bg)' }}>
                          {toolCategories
                            .filter(c => c !== 'All' && c.toLowerCase().includes(categorySearch.toLowerCase()))
                            .map(category => {
                              const active = selectedToolCategories.includes(category);
                              return (
                                <button
                                  key={category}
                                  onClick={() => setSelectedToolCategories(prev =>
                                    active ? prev.filter(c => c !== category) : [...prev, category]
                                  )}
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: 'var(--space-xs) var(--space-sm)', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', cursor: 'pointer', fontFamily: 'var(--font-primary)', fontSize: '0.9rem', color: active ? 'var(--color-primary)' : 'var(--color-text-secondary)', fontWeight: active ? '600' : '400', textAlign: 'left', transition: 'background var(--transition-speed)' }}
                                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-card)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                  {category}
                                  {active && <span style={{ color: 'var(--color-primary)', fontSize: '0.8rem' }}>✓</span>}
                                </button>
                              );
                            })}
                        </div>
                      </div>

                      {/* Free option */}
                      <div style={{ marginBottom: 'var(--space-lg)' }}>
                        <label style={{ display: 'block', marginBottom: 'var(--space-sm)', fontWeight: '700', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          Free Option?
                        </label>
                        <div style={{ display: 'inline-flex', gap: '0', border: '2px solid var(--color-primary)', borderRadius: 'var(--border-radius)', overflow: 'hidden' }}>
                          {([null, true, false] as const).map((val) => {
                            const label = val === null ? 'Any' : val ? 'Yes' : 'No';
                            const active = freeOnly === val;
                            return (
                              <button
                                key={label}
                                onClick={() => setFreeOnly(val)}
                                style={{
                                  flex: 1,
                                  padding: 'var(--space-sm) var(--space-md)',
                                  borderRadius: '0',
                                  backgroundColor: active ? 'var(--color-primary)' : 'transparent',
                                  color: active ? 'var(--color-bg)' : 'var(--color-primary)',
                                  border: 'none',
                                  fontFamily: 'var(--font-primary)',
                                  fontWeight: '600',
                                  fontSize: '1rem',
                                  cursor: 'pointer'
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Clear */}
                      {activeFilterCount > 0 && (
                        <button
                          onClick={() => { setSelectedToolCategories([]); setFreeOnly(null); }}
                          style={{
                            width: '100%',
                            padding: 'var(--space-xs)',
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-text-muted)',
                            fontFamily: 'var(--font-primary)',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Results */}
            <div style={{ marginBottom: '2rem' }}>
              <p className="text-muted">
                Showing {filteredTools.length} of {tools.length} tools
              </p>
            </div>

            {/* Tools Grid */}
            <div className="grid grid-2">
              {filteredTools.map(tool => (
                <Link key={tool.slug} to={`/tools/${tool.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}
                  onMouseEnter={e => {
                    const btn = e.currentTarget.querySelector('.compare-btn') as HTMLElement;
                    if (btn) btn.style.opacity = '1';
                  }}
                  onMouseLeave={e => {
                    const btn = e.currentTarget.querySelector('.compare-btn') as HTMLElement;
                    if (btn) btn.style.opacity = '0';
                  }}
                >
                  <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
                      <img 
                        src={`/logos/${tool.slug}.png`} 
                        alt={`${tool.name} logo`}
                        style={{ 
                          width: '48px', 
                          height: '48px', 
                          borderRadius: '8px',
                          objectFit: 'contain',
                          backgroundColor: '#FDF8F0',
                          padding: '4px'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <h3 style={{ margin: 0, flex: 1 }}>{tool.name}</h3>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/compare/${tool.slug}/_`);
                        }}
                        title="Compare this tool"
                        className="button button-primary compare-btn"
                        style={{ fontSize: '0.9rem', flexShrink: 0, opacity: 0, transition: 'opacity var(--transition-speed)' }}
                      >
                        Compare
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)', marginBottom: 'var(--space-md)' }}>
                      {tool.categories.map((category, index) => (
                        <span key={index} className="badge badge-primary" style={{ fontSize: '0.7rem' }}>
                          {category}
                        </span>
                      ))}
                    </div>

                    <p style={{ marginBottom: 'var(--space-md)' }}>{tool.description}</p>

                    <div style={{ marginBottom: 'var(--space-md)' }}>
                      <h4 style={{ fontSize: '0.9rem', marginBottom: 'var(--space-sm)' }}>Key Features:</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                        {tool.featureTags.map((feature, index) => (
                        <span
                        key={index}
                        style={{
                        fontSize: '0.8rem',
                        padding: '2px 8px',
                        backgroundColor: 'var(--color-secondary)',
                        color: 'var(--color-bg)',
                        borderRadius: '12px'
                        }}
                        >
                        {feature}
                        </span>
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <a
                        href={tool.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="button button-primary"
                        style={{ fontSize: '0.9rem' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Visit Website
                      </a>
                      <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                        Learn More →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredTools.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <h3>No tools found</h3>
                <p className="text-muted">Try adjusting your search terms or category filter.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Content Section - ChooseNews */}
      {viewMode === 'news' && (
        <section style={{ padding: '2rem 0 4rem 0' }}>
          <div className="container">
            {/* Category Filter */}
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                {newsCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedNewsCategory(category)}
                    className={selectedNewsCategory === category ? 'button button-primary' : 'button button-secondary'}
                    style={{ fontSize: '0.9rem' }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Featured Article */}
            {selectedNewsCategory === 'All' && (
              <div style={{ marginBottom: '3rem' }}>
                <Link to={`/articles/${featuredArticle.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="card" style={{ padding: 'var(--space-xl)', cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                      <span className="badge badge-primary">Featured</span>
                      <span className="text-muted">{featuredArticle.category}</span>
                    </div>
                    <h2 style={{ fontSize: '2rem', marginBottom: 'var(--space-md)' }}>
                      {featuredArticle.title}
                    </h2>
                    <p style={{ fontSize: '1.1rem', marginBottom: 'var(--space-lg)' }}>
                      {featuredArticle.excerpt}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                        <span className="text-muted">{formatLastUpdated(featuredArticle.lastUpdated)}</span>
                        <span className="text-muted">•</span>
                        <span className="text-muted">{featuredArticle.readTime}</span>
                      </div>
                      <span style={{ fontSize: '1rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                        Read Article →
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Articles Grid */}
            <div className="grid grid-2">
              {filteredArticles.slice(selectedNewsCategory === 'All' ? 1 : 0).map(article => (
                <Link key={article.id} to={`/articles/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <article className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                      <span className="badge badge-primary">{article.category}</span>
                      <span className="text-muted" style={{ fontSize: '0.9rem' }}>{article.readTime}</span>
                    </div>
                    
                    <h3 style={{ marginBottom: 'var(--space-md)' }}>
                      {article.title}
                    </h3>
                    
                    <p style={{ marginBottom: 'var(--space-lg)' }}>{article.excerpt}</p>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>{formatLastUpdated(article.lastUpdated)}</span>
                      </div>
                      <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                        Read More →
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <h3>No news found</h3>
                <p className="text-muted">Try selecting a different category.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Content Section - ChooseFlows */}
      {viewMode === 'flows' && (
        <section style={{ padding: '2rem 0 4rem 0' }}>
          <div className="container">
            <div style={{ marginBottom: '2rem' }}>
              <p className="text-muted">Showing {flows.length} flows</p>
            </div>
            <div className="grid grid-2">
              {flows.map(flow => (
                <Link key={flow.id} to={`/flows/${flow.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="card" style={{ cursor: 'pointer', transition: 'transform var(--transition-speed), box-shadow var(--transition-speed)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                      <span className="badge badge-primary" style={{ fontSize: '0.7rem' }}>ChooseFlow</span>
                    </div>
                    <h3 style={{ marginBottom: 'var(--space-sm)' }}>{flow.name}</h3>
                    <p style={{ marginBottom: 'var(--space-lg)', flex: 1 }}>{flow.oneLiner}</p>
                    {/* Tool logos */}
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-md)', flexWrap: 'wrap' }}>
                      {flow.toolsUsed.map(slug => {
                        const tool = getToolBySlug(slug);
                        if (!tool) return null;
                        return (
                          <div key={slug} title={tool.name} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 8px 3px 3px', backgroundColor: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '999px' }}>
                            <img
                              src={`/logos/${slug}.png`}
                              alt={tool.name}
                              style={{ width: '18px', height: '18px', borderRadius: '3px', objectFit: 'contain', backgroundColor: '#FDF8F0' }}
                              onError={e => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span style={{ fontSize: '0.78rem', fontWeight: '600', color: 'var(--color-text-secondary)' }}>{tool.name}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 'var(--space-lg)' }}>
                        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          <strong style={{ color: 'var(--color-text-primary)' }}>{flow.inputs.length}</strong> inputs
                        </span>
                        <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                          <strong style={{ color: 'var(--color-text-primary)' }}>{flow.outputs.length}</strong> outputs
                        </span>
                      </div>
                      <span style={{ fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>View Flow →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default HomePage;
